import { useRef, useEffect, useState } from 'react';

const PADDING = { top: 10, right: 10, bottom: 40, left: 50 };

export default function MiniBarChart({ data, height = 200, colorPositive = '#22c55e', colorNegative = '#ef4444' }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(350);

  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new ResizeObserver((entries) => {
      setWidth(entries[0].contentRect.width);
    });
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  if (!data || data.length === 0) {
    return (
      <div ref={containerRef} style={{ width: '100%', height }}>
        <svg width="100%" height={height}>
          <text x="50%" y="50%" textAnchor="middle" fill="#8b8fa3" fontSize="12">
            No data
          </text>
        </svg>
      </div>
    );
  }

  const chartW = width - PADDING.left - PADDING.right;
  const chartH = height - PADDING.top - PADDING.bottom;

  const values = data.map((d) => d.value);
  const maxAbs = Math.max(...values.map(Math.abs), 0.01);

  const barWidth = Math.min(40, (chartW / data.length) * 0.7);
  const gap = (chartW - barWidth * data.length) / (data.length + 1);

  // Zero line position
  const hasNegative = values.some((v) => v < 0);
  const zeroY = hasNegative
    ? PADDING.top + chartH * (maxAbs / (maxAbs * 2))
    : PADDING.top + chartH;

  const scaleY = hasNegative ? chartH / (maxAbs * 2) : chartH / maxAbs;

  return (
    <div ref={containerRef} style={{ width: '100%', height }}>
      <svg width={width} height={height}>
        {/* Zero line */}
        <line
          x1={PADDING.left}
          x2={width - PADDING.right}
          y1={zeroY}
          y2={zeroY}
          stroke="#2a2d3a"
          strokeWidth="1"
        />

        {/* Bars */}
        {data.map((d, i) => {
          const x = PADDING.left + gap + i * (barWidth + gap);
          const barH = Math.abs(d.value) * scaleY;
          const y = d.value >= 0 ? zeroY - barH : zeroY;
          const fill = d.color || (d.value >= 0 ? colorPositive : colorNegative);

          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={Math.max(barH, 1)}
                fill={fill}
                rx="2"
                opacity="0.8"
              />
              {/* Value label */}
              <text
                x={x + barWidth / 2}
                y={d.value >= 0 ? y - 4 : y + barH + 12}
                textAnchor="middle"
                fill="#e1e4e8"
                fontSize="10"
                fontWeight="600"
              >
                {d.value >= 0 ? '+' : ''}{d.value.toFixed(1)}
              </text>
              {/* Label */}
              <text
                x={x + barWidth / 2}
                y={height - 6}
                textAnchor="middle"
                fill="#8b8fa3"
                fontSize="9"
                transform={`rotate(-20, ${x + barWidth / 2}, ${height - 6})`}
              >
                {d.label.length > 10 ? d.label.slice(0, 10) + '…' : d.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
